import { useEffect, useMemo, useState } from "react";

import { Plus, Search } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import {
  useCreateJournal,
  useDeleteJournal,
  useJournals,
  useUpdateJournal,
} from "~/hooks/contabilidad/useJournals";
import type {
  AccountJournal,
  JournalFormData,
  JournalType,
} from "~/types/contabilidad";
import { JOURNAL_TYPE_LABELS } from "~/types/contabilidad";

import { DiarioDialog } from "./components/DiarioDialog";
import { DiarioTable } from "./components/DiarioTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function DiariosPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJournal, setEditingJournal] = useState<
    AccountJournal | undefined
  >(undefined);

  const { data: companies = [] } = useCompanies();
  const { data: journals = [], isLoading } = useJournals();

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  const createMutation = useCreateJournal();
  const updateMutation = useUpdateJournal();
  const deleteMutation = useDeleteJournal();

  const filteredJournals = useMemo(() => {
    return journals.filter((journal) => {
      const matchesSearch =
        !searchQuery ||
        journal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        journal.code.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCompany =
        companyFilter === "all" || journal.company_id === companyFilter;

      const matchesType = typeFilter === "all" || journal.type === typeFilter;

      return matchesSearch && matchesCompany && matchesType;
    });
  }, [journals, searchQuery, companyFilter, typeFilter]);

  const handleCreate = () => {
    setEditingJournal(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (journal: AccountJournal) => {
    setEditingJournal(journal);
    setDialogOpen(true);
  };

  const handleSubmit = (data: JournalFormData) => {
    if (editingJournal) {
      updateMutation.mutate(
        { id: editingJournal.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingJournal(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const journalTypes: JournalType[] = ["general", "venta", "compra", "banco"];

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Diarios</h1>
          <p className="text-sm text-muted-foreground">
            Gestión de diarios contables
          </p>
        </div>
        <Button onClick={handleCreate} className="rounded-full">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Diario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4 pt-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por código o nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl pl-9"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[200px] rounded-xl">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px] rounded-xl">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {journalTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {JOURNAL_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <DiarioTable
            journals={filteredJournals}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <DiarioDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        journal={editingJournal}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
